import {put, call} from 'redux-saga/effects';
import Conflict from "../../entities/conflict";
import * as Actions from "../actions/conflictActions";
import * as idb from '../../lib/idbd';
import * as api from '../../rest/api';
import {processQueue} from "../../queues/processQueue";
import newHRTimestamp from "../../lib/picos/timestamp";

// Inlined to reduce circular dependencies
function getCurrentUserId() {
    return sessionStorage.getItem('userId');
}

// Inlined to reduce circular dependencies
function getCurrentFirmId() {
    return sessionStorage.getItem('firmId');
}

type ConflictAction = {
    conflictId: string;
    conflict: Conflict;
    conflicts: Conflict[];
};

export function* fetchOneConflict(action: ConflictAction) {
	try {
		const conflict: Conflict = yield call(api.findOne, 'conflict', action.conflictId);

		// Now we can update the user interface
		yield put({type: Actions.CONFLICT_FETCH_ONE_SUCCESS, conflictId: action.conflictId, conflict});
	}
	catch(err) {
		yield put({type: Actions.CONFLICT_FETCH_ONE_FAILURE, conflictId: action.conflictId, status: 500, err});

		console.error('Error fetching from database', err);
	}
}

/**
 * @param {ConflictAction} action
 */
export function* saveConflict(action: ConflictAction) {
    // console.debug('⮂ CONFLICT_SAVE saga', action.conflict?.id);
    
	if (action.conflict === undefined || action.conflict === null) {
		console.error('saveConflict received a null');
		throw new Error('saveConflict received a null');
	}

	if (!(action.conflict instanceof Conflict)) {
		action.conflict = new Conflict(action.conflict);
	}

	try {
		// Save to IndexedDB
		const addResult = yield call(idb.add, 'conflict', action.conflict);
		if (addResult.isErr()) {
			console.error(`⮂ saveConflict: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.CONFLICT_SAVE_FAILURE, conflict: action.conflict, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.CONFLICT_SAVE, conflict: action.conflict});

		// Delete this entry from the cache (if there is one). Use uncacheByEntityId so we
		// catch entries regardless of which parentId the writer used (zero for grid views,
		// topLevelId for sub-task views, etc.) — the prior uncacheItem call passed entityId
		// as parentId, which never matched what writers store under, so entries leaked.
		yield call(window.storageWorker.uncacheByEntityId, getCurrentFirmId(), getCurrentUserId(), 'conflict', action.conflict.id);

		// Let the queue know to process
		yield call(processQueue);
	}
	catch(err) {
		yield put({type: Actions.CONFLICT_SAVE_FAILURE, conflict: action.conflict, status: 500, err});

		console.error('Error saving to database', err);
	}
}

/**
 * TODO: Update for saveMany
 * @param {ConflictAction} action
 * @param {Conflict[]} action.conflicts
 */
export function* saveManyConflicts(action: ConflictAction) {
    // console.debug('⮂ CONFLICT_SAVE_MANY saga', action.conflicts.map(conflict => conflict.id).join(','));
    
    if (!Array.isArray(action.conflicts)) {
        console.error('saveManyConflicts expected an array, received', action.conflicts);
        throw new Error('saveManyConflicts expected an array');
    }

	try {
		const toSave: Conflict[] = [];

		// Save each item to IndexedDB
		for(let i = 0; i < action.conflicts.length; i++) {
			const conflict = action.conflicts[i];
			conflict.persisted = false;
			toSave.push(conflict);
		}

		const addResult = yield call(idb.add, 'conflict', toSave);
		if (addResult.isErr()) {
			console.error(`⮂ saveManyConflicts: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.CONFLICT_SAVE_MANY_FAILURE, conflicts: action.conflicts, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.CONFLICT_SAVE_MANY, conflicts: toSave});

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		yield put({type: Actions.CONFLICT_SAVE_MANY_FAILURE, conflicts: action.conflicts, status: 500, err});

		console.error(`Error saving ${action.conflicts.length} conflicts to database`, err);
	}
}

/**
 * @param {ConflictAction} action
 */
export function* removeConflict(action: ConflictAction) {
    const userId = getCurrentUserId();
	if (!userId || action.conflict === undefined || action.conflict === null || !(action.conflict instanceof Conflict)) {
			console.error('removeConflict expected type Conflict, received', action.conflict);
			throw new Error('removeConflict expected type Conflict');
		}

	try {
		// Mark as deleted
		action.conflict.status = 'D';
		action.conflict.addedOn = newHRTimestamp();
		action.conflict.addedBy = userId;

		// Save to IndexedDB
		const addResult = yield call(idb.add, 'conflict', action.conflict);
		if (addResult.isErr()) {
			console.error(`⮂ removeConflict: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.CONFLICT_REMOVE_FAILURE, conflict: action.conflict, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.CONFLICT_REMOVE, conflict: action.conflict});

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		yield put({type: Actions.CONFLICT_REMOVE_FAILURE, conflict: action.conflict, status: 500, err});

		console.error('Error deleting from database', err);
	}
}

/**
	* Restore the last version of this Conflict.
	*
	* @param {ConflictAction} action
	*/
export function* restoreConflict(action: ConflictAction) {
    const userId = getCurrentUserId();
    if (!userId) {
        console.error('restoreConflict expected getCurrentUserId() to return a value');
        return;
    }
    
	if (action.conflict === undefined || action.conflict === null) {
        console.error('restoreConflict expected a Conflict, received null');
        throw new Error('restoreConflict expected a Conflict, received null');
    }
    else if (!(action.conflict instanceof Conflict)) action.conflict = new Conflict(action.conflict); 
    
	try {
		// Mark as restored
		action.conflict.status = 'U';
		action.conflict.addedOn = newHRTimestamp();
		action.conflict.addedBy = userId;
		action.conflict.restore = true;

		// Save to IndexedDB
		const addResult = yield call(idb.add, 'conflict', action.conflict);
		if (addResult.isErr()) {
			console.error(`⮂ restoreConflict: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.CONFLICT_RESTORE_FAILURE, conflict: action.conflict, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.CONFLICT_RESTORE, conflict: action.conflict});
	}
	catch(err) {
		yield put({type: Actions.CONFLICT_RESTORE_FAILURE, conflict: action.conflict, status: 500, err});

		console.error('Error restoring from database', err);
	}
}